/**
 * Plan Workouts Completed Trigger Component
 *
 * Trigger: "completed plan workouts"
 * Shows: plan select + count input
 */

import { useQuery } from '@tanstack/react-query';
import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { Box } from 'src/components/Box';
import { Stack } from 'src/components/Stack';
import { fetchWorkoutPlanOptions } from 'src/models/workout-plan/workout-plan';
import { FormSelect } from 'src/shared/Forms/Fields/FormSelect';
import { FormTextField } from 'src/shared/Forms/Fields/FormTextField';

import { TriggerComponentProps } from '../automationTriggers';

export const PlanWorkoutsCompletedTrigger: FC<TriggerComponentProps> = ({
  automation,
  index,
  readOnly,
}) => {
  const { t } = useTranslation();
  const { data: plans = [] } = useQuery({
    queryKey: ['workout-plans', 'options'],
    queryFn: () => fetchWorkoutPlanOptions(),
    staleTime: 60000,
  });

  const planOptions = plans.map((plan) => ({
    value: String(plan.id),
    label: plan.name,
  }));

  const selectedPlan = plans.find(
    (p) => String(p.id) === String(automation.reminderTypeId)
  );
  const count = automation.count || 1;

  if (readOnly) {
    const planName = selectedPlan?.name || t('automations.triggers.anyPlan');
    return (
      <span>
        {t('automations.triggers.completedPlanWorkoutsCount', { count, plan: planName })}
      </span>
    );
  }

  // Edit mode
  return (
    <Stack spacing={2}>
      <Box sx={{ maxWidth: 300 }}>
        <FormSelect
          name={`automations.${index}.reminderTypeId`}
          label={t('automations.triggerLabels.plan')}
          options={planOptions}
          prompt={t('automations.triggerLabels.selectPlan')}
        />
      </Box>
      <Box sx={{ maxWidth: 150 }}>
        <FormTextField
          name={`automations.${index}.count`}
          label={t('automations.triggerLabels.count')}
          type="number"
          InputProps={{ inputProps: { min: 1 } }}
        />
      </Box>
    </Stack>
  );
};
